import React, { useEffect, useMemo } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useGetAllFilters } from 'Deer/components/CategoryMultiSelect/dataHooks';
import { meshModules } from 'Deer/constants/modules';
import { addYears, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';

import { past3Months } from 'constants/ProjectConstants';
import { localStorageKeys } from 'constants/localStorageKeys';

import { DateRangeDropdown } from 'uiToolkitV1/DateRange';
import { MultiSelectWithCheckboxes } from 'uiToolkitV1/MultiSelectWithCheckboxes/MultiSelectWithCheckboxes';

import {
  setEngagementSelectedCompanies,
  setEngagementSelectedLocations,
  setSelectedDateRangeEngagementIntent,
  useSelectEngagementSelectedCompanies,
  useSelectEngagementSelectedIntent,
  useSelectEngagementSelectedLocations,
} from '../slice/EngagementIntentSlice';
import { EngagementSplitByDropdown } from './EngagementSplitByDropdown';
import { EngagementSurveyDropdown } from './EngagementSurveyDropdown';
import { EngagementViewByDropdown } from './EngagementViewByDropdown';
import { ViewByEmployeeFunctionDropdown } from './ViewByEmployeeFunctionDropdown';

export const setTimeBoundsToLS = (timeBounds) => {
  try {
    window.localStorage.setItem(localStorageKeys.teamPageGoalsTimeBounds, JSON.stringify(timeBounds));
  } catch (e) {
    console.error('Error in saving to LS: ', e);
  }
};

const EngagementMetaFilters = ({ hasInitialized }: { hasInitialized?: React.MutableRefObject<boolean> }) => {
  const dispatch = useDispatch();

  const {
    showTimelineDropdown,
    showSplitByDropdown,
    showSurveyDropdown,
    showViewByDropdown,
    showViewByEmployeeFunctionDropdown,
    showCompanyDropdown,
    showLocationDropdown,
  } = useSelectEngagementSelectedIntent() ?? {};

  const updateDateRange = (dateRange) => {
    dispatch(setSelectedDateRangeEngagementIntent(dateRange));
  };

  const selectedCompaniesIds = useSelectEngagementSelectedCompanies();
  const selectedLocations = useSelectEngagementSelectedLocations();

  const { data, isLoading, isSuccess } = useGetAllFilters(
    meshModules.engagement.apiValue,
    showCompanyDropdown || showLocationDropdown
  );

  const { COMPANY_ID: allCompanies = [], LOCATION: allLocations = [] } = data ?? {};

  const selectedCompanies = useMemo(
    () => selectedCompaniesIds.map((id) => allCompanies?.find((company) => company.id === id)),
    [allCompanies, selectedCompaniesIds]
  );

  useEffect(() => {
    if (isSuccess && !hasInitialized.current) {
      // settings all companies and locations to redux store
      dispatch(setEngagementSelectedCompanies(allCompanies?.map(({ id }) => id)));
      dispatch(setEngagementSelectedLocations(allLocations));
      hasInitialized.current = true;
    }
  }, [isSuccess]);

  return (
    <Flex gap="12px" flexWrap="wrap">
      <Box>{showSurveyDropdown && <EngagementSurveyDropdown />}</Box>
      {showSplitByDropdown && <EngagementSplitByDropdown />}
      {showViewByDropdown && <EngagementViewByDropdown />}
      <Box>{showViewByEmployeeFunctionDropdown && <ViewByEmployeeFunctionDropdown />}</Box>
      {showTimelineDropdown && (
        <DateRangeDropdown
          // @ts-expect-error idk :(
          onSelect={updateDateRange}
          minDate={new Date(2020, 5, 1, 0, 0, 0, 0)}
          maxDate={addYears(startOfDay(new Date()), 5)}
          initialDateRange={past3Months}
          showPredefinedRanges
          variant="chip"
        />
      )}
      {!isLoading && showCompanyDropdown && (
        <MultiSelectWithCheckboxes
          options={allCompanies.map(({ companyName, id }) => ({ label: companyName, value: id }))}
          selectedOptions={selectedCompanies.map(({ companyName, id }) => ({
            label: companyName,
            value: id,
          }))}
          onChange={(selected) => {
            dispatch(setEngagementSelectedCompanies(selected.map(({ value }) => Number(value))));
          }}
          placeholder="Select companies"
          label="Companies"
        />
      )}
      {!isLoading && showLocationDropdown && (
        <MultiSelectWithCheckboxes
          options={allLocations?.map((locationName) => ({ label: locationName, value: locationName }))}
          selectedOptions={selectedLocations.map((locationName) => ({
            label: locationName,
            value: locationName,
          }))}
          onChange={(selected) => {
            dispatch(setEngagementSelectedLocations(selected.map(({ value }) => value)));
          }}
          placeholder="Select locations"
          label="Locations"
        />
      )}
    </Flex>
  );
};

export { EngagementMetaFilters };
